import { COMP_ICON, IcAudioMuted, IcBulb, IcChart, IcCheck, IcClock, IcDl, IcHelp, IcPlay, IcPower, IcRestart } from './Icons';
import { ACTION_TONE, WEEK_TONE, dayLabel, dayTitle, actionTexts, compTone, componentWord, effectiveSound, pillDetailKey, pillLabel, seenText, stateUi, subText, titleText, visibleLog, weekLabels, whatsappLink } from './model';

// Presentational pieces of the merged screen page. Markup and class names mirror .claude/pp-screens-merged-prototype.html
// (prefixed pp-sp-*). No data fetching and no redux in here: Show.jsx wires the existing handlers/components in.

export const StatusBadge = ({ t, state, detail }) => {
  // build 402: playing without sound / loading are amber (the standby look), never the green "working" badge
  const u = stateUi(pillDetailKey(state, detail) ? 'paused' : state);
  return <span className={`pp-sp-badge pp-sp-${u.badge}`} data-testid="sh-badge" data-state={state} data-detail={detail || undefined}><span>{pillLabel(t, state, detail)}</span></span>;
};

/** inline confirm -> sent, exactly the prototype's opBox. */
export function OpBox({ t, kind, status, name, failed, onGo, onCancel }) {
  if (status === 'ask' && (kind === 'restart' || kind === 'off' || kind === 'redl' || kind === 'device')) {
    return (
      <div className="pp-sp-confirm" role="group" data-testid={`sh-confirm-${kind}`}>
        <p>{t(`sh.confirm_${kind}`, { name })}</p>
        <button type="button" className="pp-sp-btn pp-sp-danger pp-sp-sm" onClick={onGo}>{t(`sh.yes_${kind}`)}</button>
        <button type="button" className="pp-sp-btn pp-sp-ghost pp-sp-sm" onClick={onCancel}>{t('sh.cancel')}</button>
        {failed ? <span className="pp-sp-hint" role="alert">{t('sh.op_failed')}</span> : null}
      </div>
    );
  }
  if (status === 'sent') return <div className="pp-sp-done" role="status" data-testid={`sh-sent-${kind}`}><IcCheck />{t(`sh.done_${kind}`)}</div>;
  if (failed) return <div className="pp-sp-confirm" role="alert"><p>{t('sh.op_failed')}</p></div>;
  return null;
}

/** WhatsApp block. Renders NOTHING usable when no number is configured (never a broken link). */
export function SupportBox({ t, lang, support, vars, onClose }) {
  const link = whatsappLink(support, lang, vars);
  if (!link && !support?.call) return null;
  return (
    <div className="pp-sp-confirm pp-sp-wa" style={{ flexDirection: 'column', alignItems: 'stretch' }} data-testid="sh-support-block">
      {link ? (<>
        <span className="pp-sp-hint">{t('sh.wa_intro')}</span>
        <p className="pp-sp-msg">{link.text}</p>
      </>) : null}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {link ? <a className="pp-sp-btn pp-sp-primary pp-sp-sm" href={link.url} target="_blank" rel="noopener noreferrer" data-testid="sh-wa-open">{t('sh.wa_open')}</a> : null}
        <button type="button" className="pp-sp-btn pp-sp-ghost pp-sp-sm" onClick={onClose}>{t('sh.close')}</button>
      </div>
      {support?.call ? <span className="pp-sp-hint">{t('sh.wa_call')}<span className="pp-sp-ltr pp-sp-tnum">{support.call}</span></span> : null}
    </div>
  );
}

/** Status first: title + plain sentence + "what you need to do" with its one suggested action. */
export function Hero({ t, lang, c, ops, setOp, failed, support, vars, onHours, opName }) {
  const tz = c.device?.timezone;
  const act = actionTexts(t, c.action, c.hero?.title?.key);
  const tone = ACTION_TONE[c.action?.tone] || stateUi(c.state).tone;
  const supportOk = !!(support && (support.whatsapp || support.call));
  const suggest = c.action?.suggest;
  const btns = [];
  if (suggest === 'restart') btns.push(<button key="r" type="button" className="pp-sp-btn pp-sp-danger" onClick={() => setOp('restart', 'ask')}><IcRestart />{t('sh.btn_restart')}</button>);
  if (suggest === 'restart_device') btns.push(<button key="rd" type="button" className="pp-sp-btn pp-sp-danger" data-testid="sh-restart-device" onClick={() => setOp('device', 'ask')}><IcRestart />{t('sh.btn_restart_device')}</button>);
  if (suggest === 'turn_on') btns.push(<button key="on" type="button" className="pp-sp-btn pp-sp-primary" onClick={() => setOp('on', 'go')}><IcPlay />{t('sh.btn_turn_on')}</button>);
  if (suggest === 'resume') btns.push(<button key="rs" type="button" className="pp-sp-btn pp-sp-primary" data-testid="sh-resume" onClick={() => setOp('resume', 'go')}><IcPlay />{t('sh.btn_resume')}</button>);
  if (suggest === 'start_app') btns.push(<button key="st" type="button" className="pp-sp-btn pp-sp-primary" onClick={() => setOp('start', 'go')}><IcPlay />{t('sh.btn_start_app')}</button>);
  if (suggest === 'hours_settings') btns.push(<button key="h" type="button" className="pp-sp-btn pp-sp-ghost" onClick={onHours}><IcClock />{t('sh.btn_hours')}</button>);
  if (c.action?.support && supportOk) btns.push(<button key="s" type="button" className="pp-sp-btn pp-sp-ghost" onClick={() => setOp('support', 'ask')}><IcHelp />{t('sh.btn_support_short')}</button>);
  return (
    <section className="pp-sp-hero" data-testid="sh-hero" data-state={c.state}>
      <div className="pp-sp-hero-b">
        <h2>{titleText(t, c.hero?.title, tz)}</h2>
        <p>{subText(t, c.hero?.sub, tz, c.hero?.title?.params)}</p>
        <span className="pp-sp-seen">{seenText(t, c.hero?.seen, tz)}</span>
      </div>
      <div className={`pp-sp-todo pp-sp-${tone}`} data-testid="sh-act">
        <div className="pp-sp-tt"><IcBulb /><div><b>{t('sh.what_to_do')}{act.title}</b><span>{act.body}</span></div></div>
        {btns.length ? <div className="pp-sp-acts">{btns}</div> : null}
        {suggest === 'restart' ? <OpBox t={t} kind="restart" status={ops.restart} name={opName} failed={failed.restart} onGo={() => setOp('restart', 'go')} onCancel={() => setOp('restart', 'idle')} /> : null}
        {suggest === 'restart_device' ? <OpBox t={t} kind="device" status={ops.device} name={opName} failed={failed.device} onGo={() => setOp('device', 'go')} onCancel={() => setOp('device', 'idle')} /> : null}
        {suggest === 'turn_on' ? <OpBox t={t} kind="on" status={ops.on} name={opName} /> : null}
        {suggest === 'resume' ? <OpBox t={t} kind="resume" status={ops.resume} name={opName} failed={failed.resume} /> : null}
        {suggest === 'start_app' ? <OpBox t={t} kind="start" status={ops.start} name={opName} /> : null}
        {c.action?.support && supportOk && ops.support === 'ask' ? <SupportBox t={t} lang={lang} support={support} vars={vars} onClose={() => setOp('support', 'idle')} /> : null}
      </div>
    </section>
  );
}

export const Tiles = ({ t, comps, muted }) => (
  <div className="pp-sp-tiles" data-testid="sh-tiles">
    {['internet', 'sound', 'picture'].map((n) => {
      const c = n === 'sound' ? effectiveSound(comps?.sound, muted) : (comps?.[n] || { state: 'unknown', word: 'unknown' });
      const Ic = n === 'sound' && c.word === 'muted' ? IcAudioMuted : COMP_ICON[n];
      return (
        <div key={n} className={`pp-sp-tile pp-sp-${compTone(c)}`} data-comp={n} data-state={c.state}>
          <span className="pp-sp-ic"><Ic /></span>
          <span><span className="pp-sp-k">{t(`sh.${n}`)}</span><span className="pp-sp-v">{componentWord(t, n, c)}</span></span>
        </div>
      );
    })}
  </div>
);

export function DayLog({ t, lang, log, tz, day, todayDate, onToday, loading, failed }) {
  const rows = visibleLog(t, log, tz);
  const past = !!day && day !== todayDate;
  return (
    <section className="pp-sp-panel" style={{ gap: 4 }} data-testid="sh-daylog">
      <div className="pp-sp-ph">
        <h3 style={{ marginBottom: 4 }} data-testid="sh-daylog-title">{dayTitle(t, lang, day, todayDate)}</h3>
        {past ? <button type="button" className="pp-sp-btn pp-sp-ghost pp-sp-sm" data-testid="sh-daylog-today" onClick={onToday}>{t('sh.back_to_today')}</button> : null}
      </div>
      {loading ? <div className="pp-sp-skel" style={{ height: 72 }} aria-busy="true" data-testid="sh-daylog-loading" />
        : failed ? <p className="pp-sp-hint" style={{ margin: 0 }} data-testid="sh-daylog-error">{t('sh.day_error')}</p>
        : rows.length ? (
          <ol className="pp-sp-log" data-testid="sh-log">
            {rows.map((e, i) => (
              <li key={`${e.at}-${i}`} className={e.quiet ? 'pp-sp-q' : ''}>
                <span className="pp-sp-tm"><span className="pp-sp-ltr pp-sp-tnum">{e.time}</span></span>
                <span className={`pp-sp-dt pp-sp-${e.tone}`} />
                <span className="pp-sp-tx"><b>{e.text}</b>{e.sub ? <span>{e.sub}</span> : null}</span>
              </li>
            ))}
          </ol>
        ) : <p className="pp-sp-hint" style={{ margin: 0 }}>{past ? t('sh.day_empty') : t('sh.today_empty')}</p>}
    </section>
  );
}

export function Week({ t, lang, week, selected, onSelect }) {
  const words = weekLabels(t, week, lang);
  const grey = (week || []).some((d) => d.state === 'grey');
  return (
    <section className="pp-sp-panel" data-testid="sh-weekpanel">
      <h3>{t('sh.week_title')}</h3>
      <div className="pp-sp-week" data-testid="sh-week">
        {(week || []).map((d, i) => {
          const isToday = i === (week || []).length - 1;
          const on = (selected || (week || [])[(week || []).length - 1]?.date) === d.date;
          return (
            <button key={d.date} type="button" className="pp-sp-day" aria-pressed={on} data-testid={`sh-day-${d.date}`}
              aria-label={t('sh.day_pick_aria', { date: isToday ? words[i] : dayLabel(lang, d.date) })} onClick={() => onSelect && onSelect(isToday ? null : d.date)}>
              <i className={`pp-sp-${WEEK_TONE[d.state] || 'n'}`} /><small>{words[i]}</small>
            </button>
          );
        })}
      </div>
      <div className="pp-sp-legend">
        <span><i style={{ background: 'var(--pp-comp-good-soft)', border: '1px solid var(--pp-btn-jade)' }} />{t('sh.legend_ok')}</span>
        <span><i style={{ background: 'var(--pp-comp-crit-soft)', border: '1px solid var(--pp-btn-coral)' }} />{t('sh.legend_bad')}</span>
        {grey ? <span><i style={{ background: 'var(--pp-comp-surface-2)', border: '1px solid var(--pp-comp-line-strong)' }} />{t('sh.legend_none')}</span> : null}
      </div>
    </section>
  );
}

/** Screen tools: restart / turn off-on / download again / rotation (existing component slot) / operating hours. */
export function Tools({ t, c, ops, setOp, failed, opName, rotation, hours, onEditHours, canPower, muted, canMute }) {
  const isOff = c.state === 'standby' || c.state === 'app_closed';
  const suggestRestart = c.action?.suggest === 'restart';
  return (
    <section className="pp-sp-panel" style={{ gap: 0 }} data-testid="sh-tools">
      <h3 style={{ marginBottom: 6 }}>{t('sh.tools_title')}</h3>
      <div className="pp-sp-tool"><div className="pp-sp-trow"><b>{t('sh.tool_restart_t')}</b><button type="button" className="pp-sp-btn pp-sp-ghost pp-sp-sm" onClick={() => setOp('restart', 'ask')}><IcRestart />{t('sh.tool_restart_btn')}</button></div>
        <span className="pp-sp-ds">{t('sh.tool_restart_d')}</span>
        {suggestRestart ? null : <OpBox t={t} kind="restart" status={ops.restart} name={opName} failed={failed.restart} onGo={() => setOp('restart', 'go')} onCancel={() => setOp('restart', 'idle')} />}
      </div>
      {canPower ? (
        <div className="pp-sp-tool"><div className="pp-sp-trow"><b>{t(isOff ? 'sh.tool_on_t' : 'sh.tool_off_t')}</b>
          {isOff
            ? <button type="button" className="pp-sp-btn pp-sp-ghost pp-sp-sm" onClick={() => setOp(c.state === 'app_closed' ? 'start' : 'on', 'go')}><IcPlay />{t('sh.btn_turn_on_short')}</button>
            : <button type="button" className="pp-sp-btn pp-sp-ghost pp-sp-sm" onClick={() => setOp('off', 'ask')}><IcPower />{t('sh.btn_turn_off')}</button>}</div>
          <span className="pp-sp-ds">{t('sh.tool_off_d')}</span>
          <OpBox t={t} kind="off" status={ops.off} name={opName} failed={failed.off} onGo={() => setOp('off', 'go')} onCancel={() => setOp('off', 'idle')} />
          {c.action?.suggest === 'turn_on' || c.action?.suggest === 'start_app' ? null : <OpBox t={t} kind="on" status={ops.on} name={opName} />}
        </div>
      ) : null}
      <div className="pp-sp-tool"><div className="pp-sp-trow"><b>{t('sh.tool_redl_t')}</b><button type="button" className="pp-sp-btn pp-sp-ghost pp-sp-sm" onClick={() => setOp('redl', 'ask')}><IcDl />{t('sh.tool_redl_btn')}</button></div>
        <span className="pp-sp-ds">{t('sh.tool_redl_d')}</span>
        <OpBox t={t} kind="redl" status={ops.redl} name={opName} failed={failed.redl} onGo={() => setOp('redl', 'go')} onCancel={() => setOp('redl', 'idle')} />
      </div>
      {canMute && !isOff && c.state !== 'offline' && c.state !== 'limited' ? (
        <div className="pp-sp-tool" data-testid="sh-sound-tool"><div className="pp-sp-trow"><b>{t('sh.tool_sound_t')}</b>
          {/* the button NAMES the action it performs ("Mute" / "Unmute"); the state icon is never the only signal */}
          <button type="button" className="pp-sp-btn pp-sp-ghost pp-sp-sm" data-testid="sh-mute-btn" data-action={muted ? 'unmute' : 'mute'} onClick={() => setOp(muted ? 'unmute' : 'mute', 'go')}>
            <IcAudioMuted />{t(muted ? 'sh.btn_unmute' : 'sh.btn_mute')}</button></div>
          <span className="pp-sp-ds">{t(muted ? 'sh.tool_sound_d_muted' : 'sh.tool_sound_d_on')}</span>
          <OpBox t={t} kind="mute" status={ops.mute} name={opName} failed={failed.mute} />
          <OpBox t={t} kind="unmute" status={ops.unmute} name={opName} failed={failed.unmute} />
        </div>
      ) : null}
      {rotation ? <div className="pp-sp-tool" data-testid="sh-rotation">{rotation}</div> : null}
      <div className="pp-sp-tool"><div className="pp-sp-trow"><b>{t('sh.tool_hours_t')}</b><button type="button" className="pp-sp-btn pp-sp-ghost pp-sp-sm" onClick={onEditHours}>{t('sh.edit')}</button></div>
        <span className="pp-sp-ds pp-sp-tnum">{hours}</span></div>
    </section>
  );
}

export const PerfLink = ({ t, enabled, onClick }) => (
  <button className="pp-sp-perf" type="button" onClick={onClick} disabled={!enabled} style={enabled ? undefined : { opacity: 0.6, cursor: 'default' }} data-testid="sh-perf">
    <span className="pp-sp-pi"><IcChart /></span><span><b>{t('sh.perf_t')}</b><span>{t('sh.perf_d')}</span></span>
  </button>
);

export const Details = ({ t, rows }) => (
  <section className="pp-sp-panel" style={{ gap: 0 }} data-testid="sh-details">
    <h3 style={{ marginBottom: 6 }}>{t('sh.details_title')}</h3>
    {rows.map((r) => (<div className="pp-sp-kv" key={r.k}><span>{r.k}</span><span>{r.v}</span></div>))}
  </section>
);

export const HelpPanel = ({ t, lang, support, vars, ops, setOp }) => {
  if (!support || !(support.whatsapp || support.call)) return null; // no number configured -> nothing at all
  return (
    <section className="pp-sp-panel" data-testid="sh-help">
      <h3>{t('sh.tool_help_t')}</h3>
      <span className="pp-sp-ds" style={{ fontSize: 14, color: 'var(--pp-comp-ink-2)' }}>{t('sh.help_d')}</span>
      <button type="button" className="pp-sp-btn pp-sp-ghost" onClick={() => setOp('support2', 'ask')}><IcHelp />{t('sh.btn_support')}</button>
      {ops.support2 === 'ask' ? <SupportBox t={t} lang={lang} support={support} vars={vars} onClose={() => setOp('support2', 'idle')} /> : null}
    </section>
  );
};
